import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Input from "../components/form/Input";
import ButtonSecondary from "../components/buttons/ButtonSecondary";
import { loginFields } from "../constants/formFields";
import Cookies from "js-cookie";
import { Hourglass } from "react-loader-spinner";
import "react-toastify/dist/ReactToastify.css";
import useAuthApi from "../hooks/api/useAuthApi";

const REQUIRED_LOGIN_FIELDS = loginFields.filter((field) => field.isRequired);
const getInitialFormState = () =>
  loginFields.reduce((acc, field) => {
    acc[field.name] = "";
    return acc;
  }, {});

const LoginPage = () => {
  const redirectTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login } = useAuthApi();

  // Initialize form data from loginFields
  const [formData, setFormData] = useState(getInitialFormState);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  }, []);

  const handleCheckboxChange = useCallback((e) => {
    setRememberMe(e.target.checked);
  }, []);

  const validateForm = useCallback(() => {
    // Check if all required fields are filled
    for (const field of REQUIRED_LOGIN_FIELDS) {
      if (!formData[field.name] || formData[field.name].trim() === "") {
        toast.error(`${field.labelText} is required`);
        return false;
      }
    }

    // Check email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }

    return true;
  }, [formData]);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    let shouldResetLoading = true;

    try {
      // Direct API call to login endpoint
      const response = await login({
        email: formData.email,
        password: formData.password
      });

      if (response && response.status === 200) {
        shouldResetLoading = false;
        const { accessToken: token } = response.data;

        // Set cookie with expiration based on Remember Me
        const cookieExpiration = rememberMe ? 7 : 1 / 24; // 7 days or 1 hour
        Cookies.set("AccessToken", token, { expires: cookieExpiration });

        toast.success("Login successful! Redirecting...");

        // Redirect to dashboard after a short delay
        redirectTimeoutRef.current = setTimeout(() => {
          navigate("/dashboard", { replace: true });
        }, 1000);
        return;
      }
    } catch (error) {
      console.error("Login error:", error);

      // Handle different error scenarios
      if (error.response) {
        const { status, data } = error.response;

        if (status === 404) {
          if (data && data.errors) {
            // Multiple validation errors
            Object.values(data.errors).forEach((errorArray) => {
              if (Array.isArray(errorArray)) {
                errorArray.forEach(msg => toast.error(msg));
              }
            });
          } else {
            toast.error(data.error || "Resource not found");
          }
        } else if (status === 422) {
          // Validation errors
          if (data && data.errors) {
            Object.values(data.errors).forEach((errorArray) => {
              if (Array.isArray(errorArray)) {
                errorArray.forEach(msg => toast.error(msg));
              }
            });
          } else {
            toast.error(data.error || "Validation error");
          }
        } else if (status === 401) {
          toast.error("Invalid email or password");
        } else {
          toast.error(data.error || "An error occurred during login");
        }
      } else if (error.request) {
        // Request was made but no response received
        toast.error("Unable to connect to server. Please check your internet connection.");
      } else {
        // Something else happened
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      if (shouldResetLoading) {
        setIsLoading(false);
      }
    }
  }, [validateForm, formData.email, formData.password, rememberMe, navigate, login]);

  const existingToken = Cookies.get("AccessToken");
  if (existingToken) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="card-login bg-white rounded-lg mobXS:mx-6 tabMD:mx-6 my-10 border desktopXl:w-1/4 desktop:w-1/2 desktop:mx-auto">
      <h6 className="text-h6 text-dark4 text-center py-12 px-8">
        Log in to your account
      </h6>

      {isLoading ? (
        <div className="flex flex-row justify-center items-center left-0 right-0 mx-auto pb-20">
          <Hourglass
            visible={true}
            height="40"
            width="40"
            animationDuration="2"
            ariaLabel="hourglass-loading"
            wrapperStyle={{}}
            wrapperClass=""
            colors={["#FF5400", "#FF5400"]}
          />
        </div>
      ) : (
        <form className="space-y-6" onSubmit={handleLogin}>
          <div className="space-y-px desktop:w-full flex justify-center">
            <div className="mobXS:w-full tabMD:w-1/2 desktop:w-full tabMD:items-center">
              {loginFields.map((field) => (
                <Input
                  key={field.id}
                  handleChange={handleChange}
                  value={formData[field.name] || ""}
                  labelText={field.labelText}
                  labelFor={field.labelFor}
                  id={field.id}
                  name={field.name}
                  type={field.type}
                  isRequired={field.isRequired}
                  placeholder={
                    typeof field.placeholder === "string"
                      ? field.placeholder
                      : undefined
                  }
                  customClass="rounded-lg"
                  disabled={isLoading}
                />
              ))}

              <div className="flex items-center mx-6 mt-4">
                <input
                  type="checkbox"
                  className="rounded border-stroke text-primary cursor-pointer"
                  checked={rememberMe}
                  onChange={handleCheckboxChange}
                  disabled={isLoading}
                  id="remember-me-checkbox"
                />
                <label
                  htmlFor="remember-me-checkbox"
                  className="text-dark1 text-bodyMM ml-3 cursor-pointer"
                >
                  Remember me
                </label>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-y-6 px-6 mobXS:w-full tabMD:w-1/2 desktop:w-full mobXS:items-start mobXS:justify-self-start tabMD:items-center tabMD:justify-self-center">
            <ButtonSecondary
              text={isLoading ? "Signing in..." : "Sign in"}
              type="submit"
              bgColor="bg-secondary"
              color="text-white"
              padding="mobXS:py-[10px] mobXS:px-[24px] tabMD:py-[10px] tabMD:px-[24px]"
              customClass="rounded-[25px] justify-center"
              fontsize="text-bodyMR"
              width="mobXS:w-full tabMD:w-1/2 desktop:w-full"
              disabled={isLoading}
            />

            <ButtonSecondary
              borderColor="border-primary"
              text="Sign in with Google"
              fontsize="text-bodyMR"
              padding="py-[10px] px-[24px]"
              color="text-dark3"
              bgColor="bg-white"
              width="w-full"
              iconType="google"
              customClass="justify-center"
              disabled={isLoading}
            />

            <ButtonSecondary
              borderColor="border-primary"
              text="Sign in with Apple"
              fontsize="text-bodyMR"
              padding="py-[10px] px-[24px]"
              color="text-dark3"
              bgColor="bg-white"
              width="w-full"
              iconType="apple"
              customClass="justify-center"
              disabled={isLoading}
            />
          </div>

          <div className="flex justify-center">
            <a
              className="text-primary text-bodyMM hover:underline"
              href="/forgot-password"
            >
              Forgot password?
            </a>
          </div>

          <div className="flex justify-center pb-10">
            <p className="text-dark6 text-bodyMM">
              Don't have an account?
              <a
                className="text-primary text-bodyMM ml-2 hover:underline"
                href="/register"
              >
                Sign up now
              </a>
            </p>
          </div>
        </form>
      )}

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default LoginPage;